import { ajouterMois, ecartJours, formaterJour, formaterMontant } from '@admin-pmvb/core'
import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import type { Echeance, Paiement, Tache } from '../api'
import { jourCourt, LIBELLES_RETARD, LIBELLES_STATUT_ECHEANCE, moisCourt } from '../libelles'
import { Icone } from './Icone'

// Frise sur douze mois, une ligne par entité : losange pour une date fixe, bande pour
// une fenêtre, contour pointillé pour « à confirmer », couleur par statut, ligne
// « aujourd'hui ». Survol ou focus : infobulle avec libellé, dates, montant, responsables.

export type LigneFrise = { entiteId: string; nom: string; responsables: string }

export function Frise({
  lignes,
  echeances,
  debut,
  aujourdhui,
  retards = new Set(),
  paiements = [],
  taches = [],
}: {
  lignes: LigneFrise[]
  echeances: Echeance[]
  /** Échéances dont le paiement est en retard (indicateur calculé) : en rouge. */
  retards?: Set<string>
  /** Surcouche des paiements attendus (petits disques), si demandée. */
  paiements?: Paiement[]
  /** Surcouche des tâches ouvertes (petits carrés), si demandée. */
  taches?: Tache[]
  /** Premier jour du premier mois affiché. */
  debut: string
  aujourdhui: string
}) {
  const fin = ajouterMois(debut, 12)
  const total = ecartJours(debut, fin)
  const position = (jour: string) =>
    Math.min(100, Math.max(0, (ecartJours(debut, jour) / total) * 100))
  const mois = Array.from({ length: 12 }, (_, i) => ajouterMois(debut, i))
  const [survol, setSurvol] = useState<string | null>(null)

  return (
    <div className="frise-defilement">
      <div className="frise" role="table" aria-label="Frise des échéances sur douze mois">
        <div className="frise-ligne frise-entete" role="row">
          <div className="frise-nom" role="columnheader">
            Entité
          </div>
          <div className="frise-piste" role="columnheader">
            {mois.map((m) => (
              <span key={m} className="frise-mois" style={{ left: `${position(m)}%` }}>
                <span className="annee">{m.slice(5, 7) === '01' ? m.slice(0, 4) : '\u00a0'}</span>
                {moisCourt(m)}
              </span>
            ))}
          </div>
        </div>
        {lignes.map((ligne, rang) => (
          <div key={ligne.entiteId} className="frise-ligne" role="row">
            <div className="frise-nom" role="rowheader">
              {ligne.nom}
              {ligne.responsables && <span className="mono">{ligne.responsables}</span>}
            </div>
            <div className="frise-piste" role="cell">
              {mois.map((m) => (
                <span key={m} className="frise-grille" style={{ left: `${position(m)}%` }} />
              ))}
              <span className="frise-aujourdhui" style={{ left: `${position(aujourdhui)}%` }} />
              {echeances
                .filter((e) => e.entiteId === ligne.entiteId && e.date < fin)
                .map((e) => {
                  const gauche = position(e.date)
                  const fenetre = e.finFenetre !== null && e.finFenetre > e.date
                  const titre = [
                    `${e.libelle} (${e.periode})`,
                    fenetre
                      ? `du ${formaterJour(e.date)} au ${formaterJour(e.finFenetre!)}`
                      : formaterJour(e.date),
                    e.montantPrevuCts !== null
                      ? formaterMontant(e.montantPrevuCts, e.devise)
                      : null,
                    LIBELLES_STATUT_ECHEANCE[e.statut],
                    e.certitude === 'a_confirmer' ? 'à confirmer' : null,
                    retards.has(e.id) ? 'paiement en retard' : null,
                    ligne.responsables ? `responsables : ${ligne.responsables}` : null,
                  ]
                    .filter(Boolean)
                    .join('\n')
                  const classes = [
                    'frise-item',
                    fenetre ? 'frise-bande' : 'frise-losange',
                    `statut-${e.statut}`,
                    e.certitude === 'a_confirmer' ? 'a-confirmer' : '',
                    retards.has(e.id) ? 'en-retard' : '',
                  ].join(' ')
                  const dates = fenetre
                    ? `du ${jourCourt(e.date)} au ${jourCourt(e.finFenetre!)} ${e.finFenetre!.slice(0, 4)}`
                    : `${jourCourt(e.date)} ${e.date.slice(0, 4)}`
                  const ouvrir = () => setSurvol(e.id)
                  const fermer = () => setSurvol((s) => (s === e.id ? null : s))
                  return (
                    <span key={e.id} className="contents">
                      <Link
                        to="/echeances/$id"
                        params={{ id: e.id }}
                        className={classes}
                        aria-label={titre}
                        onMouseEnter={ouvrir}
                        onMouseLeave={fermer}
                        onFocus={ouvrir}
                        onBlur={fermer}
                        style={
                          fenetre
                            ? {
                                left: `${gauche}%`,
                                width: `${Math.max(0.6, position(e.finFenetre!) - gauche)}%`,
                              }
                            : { left: `${gauche}%` }
                        }
                      />
                      {survol === e.id && (
                        <div
                          className="frise-infobulle"
                          role="tooltip"
                          style={{
                            ...(gauche > 80
                              ? { right: `${100 - gauche}%` }
                              : { left: `${gauche}%` }),
                            ...(rang < 2
                              ? { top: 'calc(50% + 14px)' }
                              : { bottom: 'calc(50% + 14px)' }),
                          }}
                        >
                          <strong>
                            {e.libelle} ({e.periode})
                          </strong>
                          <span>
                            <Icone nom="calendar_today" />
                            {dates}
                          </span>
                          {e.montantPrevuCts !== null && (
                            <span>
                              <Icone nom="payments" />
                              {formaterMontant(e.montantPrevuCts, e.devise)}
                            </span>
                          )}
                          {ligne.responsables && (
                            <span>
                              <Icone nom="group" />
                              {ligne.responsables}
                            </span>
                          )}
                          <div className={`frise-infobulle-pied statut-${e.statut}`}>
                            <span
                              className={`pastille ${retards.has(e.id) ? 'pastille-retard' : ''}`}
                            />
                            {retards.has(e.id)
                              ? 'Paiement en retard'
                              : LIBELLES_STATUT_ECHEANCE[e.statut]}
                            {e.certitude === 'a_confirmer' && (
                              <span className="confirmer">à confirmer</span>
                            )}
                          </div>
                        </div>
                      )}
                    </span>
                  )
                })}
              {paiements
                .filter(
                  (p) =>
                    p.entiteId === ligne.entiteId &&
                    p.datePrevue &&
                    p.datePrevue >= debut &&
                    p.datePrevue < fin,
                )
                .map((p) => {
                  const titre = [
                    `${p.sens === 'payer' ? 'À payer' : 'À encaisser'} : ${p.libelle ?? p.echeance ?? ''}`,
                    formaterJour(p.datePrevue!),
                    formaterMontant(p.resteCts, p.devise),
                    LIBELLES_RETARD[p.retard] || null,
                  ]
                    .filter(Boolean)
                    .join('\n')
                  return (
                    <span
                      key={p.id}
                      className={`frise-paiement ${p.retard === 'en_retard' ? 'en-retard' : ''}`}
                      style={{ left: `${position(p.datePrevue!)}%` }}
                      title={titre}
                      aria-label={titre}
                    />
                  )
                })}
              {taches
                .filter(
                  (t) =>
                    t.entiteId === ligne.entiteId &&
                    t.dateDue &&
                    t.dateDue >= debut &&
                    t.dateDue < fin,
                )
                .map((t) => {
                  const titre = [
                    `Tâche : ${t.titre}`,
                    formaterJour(t.dateDue!),
                    t.executant ?? t.executantTiers ?? 'à prendre',
                    t.enRetard ? 'en retard' : null,
                  ]
                    .filter(Boolean)
                    .join('\n')
                  return (
                    <Link
                      key={t.id}
                      to="/taches/$id"
                      params={{ id: t.id }}
                      className={`frise-tache ${t.enRetard ? 'en-retard' : ''}`}
                      style={{ left: `${position(t.dateDue!)}%` }}
                      title={titre}
                      aria-label={titre}
                    />
                  )
                })}
            </div>
          </div>
        ))}
      </div>
      <ul className="frise-legende" aria-label="Légende">
        <li>
          <span className="frise-item frise-losange statut-a_venir legende" /> date fixe
        </li>
        <li>
          <span className="frise-item frise-bande statut-a_venir legende" /> fenêtre
        </li>
        <li>
          <span className="frise-item frise-losange statut-a_venir a-confirmer legende" /> à
          confirmer
        </li>
        {(['a_venir', 'en_preparation', 'faite', 'sans_objet'] as const).map((s) => (
          <li key={s}>
            <span className={`pastille statut-${s}`} /> {LIBELLES_STATUT_ECHEANCE[s].toLowerCase()}
          </li>
        ))}
        <li>
          <span className="pastille pastille-retard" /> paiement en retard
        </li>
        {paiements.length > 0 && (
          <li>
            <span className="frise-paiement legende" /> paiement attendu
          </li>
        )}
        {taches.length > 0 && (
          <li>
            <span className="frise-tache legende" /> tâche
          </li>
        )}
      </ul>
    </div>
  )
}
